import { Building2, CheckCircle2, AlertTriangle, XCircle, Link2, Search } from 'lucide-react';
import type { AuditSummary } from '@claro/shared';

interface AuditKpiCardsProps {
  summary: AuditSummary | undefined;
  isLoading: boolean;
}

interface KpiItem {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

export function AuditKpiCards({ summary, isLoading }: AuditKpiCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[100px] animate-pulse rounded-lg border bg-card" />
        ))}
      </div>
    );
  }

  if (!summary) return null;
  const unitCnpjPairs = summary.matchedUnitCnpjPairs ?? summary.matched;

  const items: KpiItem[] = [
    {
      title: 'XLSM Ativos',
      value: summary.totalTaxActive,
      subtitle: 'Estabelecimentos no tributário',
      icon: <Building2 className="h-4 w-4" />,
      color: 'text-blue-500',
    },
    {
      title: 'Cruzados',
      value: unitCnpjPairs,
      subtitle: `${summary.matched} CNPJs em ${summary.totalPdUnits} unidades PD`,
      icon: <Link2 className="h-4 w-4" />,
      color: 'text-indigo-500',
    },
    {
      title: 'Sem Divergência',
      value: summary.severityBreakdown[0] ?? 0,
      subtitle: 'Severi­dade 0',
      icon: <CheckCircle2 className="h-4 w-4" />,
      color: 'text-emerald-500',
    },
    {
      title: 'Divergentes',
      value: unitCnpjPairs - (summary.severityBreakdown[0] ?? 0),
      subtitle: 'Severidade 1–5',
      icon: <AlertTriangle className="h-4 w-4" />,
      color: 'text-amber-500',
    },
    {
      title: 'Problemáticos',
      value: `${summary.problematicUnits} (${summary.problematicPercent}%)`,
      subtitle: 'Severidade 4 + 5',
      icon: <XCircle className="h-4 w-4" />,
      color: 'text-red-500',
    },
    {
      title: 'Não encontrados',
      value: summary.notFoundInPd,
      subtitle: 'XLSM sem match no PD',
      icon: <Search className="h-4 w-4" />,
      color: 'text-slate-500',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
      {items.map((item) => (
        <div
          key={item.title}
          className="flex flex-col justify-between rounded-lg border bg-card p-3"
        >
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={item.color}>{item.icon}</span>
            {item.title}
          </div>
          <div className="mt-1 text-xl font-bold">{item.value}</div>
          {item.subtitle && (
            <div className="text-[10px] text-muted-foreground">{item.subtitle}</div>
          )}
        </div>
      ))}
    </div>
  );
}
